import type { DerivedCampaignStatus } from "src/graphql";
import type { JourneyState } from "src/pages/journeys/types";
import { ChannelType } from "src/types/cep";

import type {
  SurfaceEditability,
  SurfaceInput,
} from "@hightouch/lib/cep/surface-editability";

export type {
  ContentSaveMode,
  ContentEditability,
  SurfaceEditability,
  SurfaceInput,
} from "@hightouch/lib/cep/surface-editability";
export {
  isStructurallyEditableJourneyState,
  getJourneyContentSaveMode,
  deriveSurfaceEditability,
} from "@hightouch/lib/cep/surface-editability";

export type MetadataDrawerMode = "full" | "stage" | "settings";

/**
 * Read by both the metadata route gate and the drawer so they can't disagree on
 * where a save lands. `null` means bounce:
 * - In-app has no metadata fields at all, so there is nothing to open.
 * - A surface that publishes content immediately but isn't Draft would strand a
 *   stage a draft nothing would publish.
 * - Staging carries only subject/preheader, which exist on email alone.
 */
export function getMetadataDrawerMode(
  surface: SurfaceEditability,
  channelType: ChannelType | null,
  {
    hasMessageTypeConflict,
  }: {
    /** The message's type no longer matches what its content requires (`useMessageTypeConflict`). */
    hasMessageTypeConflict: boolean;
  },
): MetadataDrawerMode | null {
  if (!surface.content.canEdit) return null;
  // In-app has no message-level metadata to edit: no subject or preheader, and
  // no consent model, so no message type, subscription topic, or sender.
  if (channelType === ChannelType.InApp) return null;
  if (surface.isDraft) return "full";
  // A paused campaign can fix only a message type its content no longer allows.
  if (surface.canEditSettings && hasMessageTypeConflict) return "settings";
  if (surface.content.saveMode !== "stage") return null;
  if (channelType !== ChannelType.Email) return null;
  return "stage";
}

/**
 * Maps the message outlet's raw trigger data to a {@link SurfaceInput}. Campaign
 * takes precedence (matches the loader's prior behavior); the two are mutually
 * exclusive in practice. A realtime journey's message is locked in every state.
 */
export function messageSurfaceInput(args: {
  journeyState: JourneyState | null;
  realtimeJourney: boolean;
  campaignInfo: { derivedStatus: DerivedCampaignStatus | undefined } | null;
}): SurfaceInput {
  if (args.campaignInfo != null) {
    return {
      trigger: "campaign",
      derivedStatus: args.campaignInfo.derivedStatus,
    };
  }
  if (args.journeyState != null) {
    return args.realtimeJourney
      ? { trigger: "realtimeJourney" }
      : { trigger: "journey", journeyState: args.journeyState };
  }
  return { trigger: "standalone" };
}
